import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useLocale } from 'src/app/shared/hooks/l10n';

import type { ParsedPromotion } from '../../types';

type UseRecentPromotionsListReturn = ModelHookResult<{ promotions: ParsedPromotion[] }>;
export function useRecentPromotionsList(): UseRecentPromotionsListReturn {
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const i18n = useI18n();
	const locale = useLocale();

	const sortedPromotions = useMemo(() => {
		const recentPromotions = petition.recentNonRefundedPromotions ?? [];
		const freePromotions = petition.freePromotions ?? [];

		// Deduplicate by id (freePromotions limit 2 can overlap with recentPromotions limit 20)
		const seen = new Set<string>();
		const combined = [...freePromotions, ...recentPromotions].filter((p) => {
			if (seen.has(p.id)) return false;
			seen.add(p.id);
			return true;
		});

		return combined.sort((a, b) => -a.createdAt.localeCompare(b.createdAt, locale));
	}, [petition.freePromotions, petition.recentNonRefundedPromotions, locale]);

	const transformedPromotions = useMemo(() => {
		return sortedPromotions.map((promotion) => {
			const { amount, amountLocalizedWithCurrency, user, fulfilled, stats, ...rest } = promotion;

			const amountFormatted =
				amount === 0
					? i18n.translate('corgi.petition.starter_dashboard.petition_insights.promotion_history.card.free')
					: amountLocalizedWithCurrency;
			const userDisplayName = user
				? user.displayName
				: i18n.translate(
						'corgi.petition.starter_dashboard.petition_insights.promotion_history.card.default_anonymous_name',
					);
			const statsFormatted = stats && {
				...stats,
				// once fulfilled, a promotion can then become "overfulfilled", in this case, we will use the `impressionsServed` for total instead
				impressionsTotal: fulfilled ? stats.impressionsServed : stats.impressionsTotal,
				impressionsTotalLocalized: fulfilled ? stats.impressionsServedLocalized : stats.impressionsTotalLocalized,
			};

			return {
				...rest,
				amountFormatted,
				fulfilled,
				user: {
					displayName: userDisplayName,
					photo: user?.photo?.userSmall.url,
				},
				stats: statsFormatted,
			};
		});
	}, [i18n, sortedPromotions]);

	return {
		data: { promotions: transformedPromotions },
	};
}
